import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { useDispatch } from 'react-redux';
import '../../Advertiser/AdvertiserSignup/AdvertiserSignup.css';
import '../../Advertiser/AdvertiserActivate/AdvertiserActivate.css';
import {
    getScreenHostSignupStoreTypes,
    screenHostSignupSendCode,
    screenHostSignupVerifyCode,
    screenHostSignup,
} from '../../../services/screenHostSignup';
import { getHostAgreement } from '../../../services/getHostAgreement';
import { save } from '../../../features/token/tokenSaver';
import { persistUserSession } from '../../../services/userSession';
import { persistRoleSession } from '../../../services/authSession';
import posthog from '../../../services/posthog';
import ScreenInstallCarousel from '../../Advertiser/AdvertiserSignup/ScreenInstallCarousel';
import SegmentedOtpInput from '../../Advertiser/AdvertiserSignup/SegmentedOtpInput';

const PROGRESS_LABELS = ['Welcome', 'Store', 'Type', 'Contact', 'Verify', 'Password', 'Agreement'];
const TOTAL_STEPS = PROGRESS_LABELS.length;
const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;

const SCREEN_COUNT_OPTIONS = Array.from({ length: 20 }, (_, i) => ({
    label: String(i + 1),
    value: i + 1,
}));

function SignupNav({ onLogoClick }) {
    return (
        <nav className="advertiser-activate-nav">
            <button type="button" className="advertiser-activate-nav-brand" onClick={onLogoClick}>
                <span className="advertiser-activate-nav-wordmark">INVERT</span>
            </button>
        </nav>
    );
}

export default function ScreenHostSignup() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [currentStep, setCurrentStep] = useState(0);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [storeTypes, setStoreTypes] = useState([]);
    const [selectedStoreType, setSelectedStoreType] = useState(null);
    const [selectedScreenCount, setSelectedScreenCount] = useState({ label: '1', value: 1 });
    const [emailVerified, setEmailVerified] = useState(false);
    const [emailVerificationToken, setEmailVerificationToken] = useState(null);
    const [otpCode, setOtpCode] = useState('');
    const [sendingCode, setSendingCode] = useState(false);
    const [verifyingCode, setVerifyingCode] = useState(false);
    const [codeSent, setCodeSent] = useState(false);
    const [agreementAccepted, setAgreementAccepted] = useState(false);
    const [agreementVersion, setAgreementVersion] = useState(null);
    const [hardwareSetupPreference, setHardwareSetupPreference] = useState('smart_tv');
    const [form, setForm] = useState({
        businessName: '',
        businessAddress: '',
        businessCity: '',
        businessState: '',
        businessZipCode: '',
        storeTypeNotes: '',
        fname: '',
        lname: '',
        email: '',
        phoneNumber: '',
        jobTitle: '',
        password: '',
        confirmPassword: '',
    });

    const isOtherStoreType = useMemo(
        () => selectedStoreType?.label === 'Other',
        [selectedStoreType]
    );

    useEffect(() => {
        if (currentStep === 4 && !codeSent && !emailVerified && !sendingCode && form.email.trim()) {
            handleSendCode();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep]);

    useEffect(() => {
        posthog.capture('screen host signup started');
        getScreenHostSignupStoreTypes()
            .then((res) => {
                const options = (res.data?.storeTypes || []).map((c) => ({
                    label: c.TypeName,
                    value: c.StoreTypeId,
                }));
                setStoreTypes(options);
            })
            .catch(() => setStoreTypes([]));

        getHostAgreement()
            .then((res) => {
                if (res.data?.success) {
                    setAgreementVersion(res.data.version);
                }
            })
            .catch(() => {});
    }, []);

    function onChange(field, value) {
        setForm((prev) => ({ ...prev, [field]: value }));
        if (field === 'email') {
            setEmailVerified(false);
            setEmailVerificationToken(null);
            setCodeSent(false);
            setOtpCode('');
        }
    }

    function validateStep(step) {
        if (step === 1) {
            if (!form.businessName.trim()) return 'Business name is required';
            if (!form.businessAddress.trim()) return 'Business address is required';
            if (!form.businessCity.trim()) return 'City is required';
            if (!form.businessState.trim()) return 'State is required';
            if (!form.businessZipCode.trim()) return 'Zip code is required';
        }
        if (step === 2) {
            if (!selectedStoreType) return 'Please select a store type';
            if (isOtherStoreType && !form.storeTypeNotes.trim()) {
                return 'Please describe your store type';
            }
        }
        if (step === 3) {
            if (!form.fname.trim() || !form.lname.trim()) return 'First and last name are required';
            if (!form.email.trim() || !EMAIL_REGEX.test(form.email.trim())) return 'Valid email is required';
        }
        if (step === 4) {
            if (!emailVerified || !emailVerificationToken) return 'Please verify your email before continuing';
        }
        if (step === 5) {
            if (!form.password || form.password.length < 8) return 'Password must be at least 8 characters';
            if (form.password !== form.confirmPassword) return 'Passwords do not match';
        }
        if (step === 6) {
            if (!agreementAccepted) return 'You must accept the INVERT Screen Host Agreement';
            if (!agreementVersion) return 'Could not load agreement version. Please refresh.';
            if (!hardwareSetupPreference) return 'Please select how you will run INVERT on your TVs';
        }
        return '';
    }

    function goNext() {
        const msg = validateStep(currentStep);
        if (msg) {
            setError(msg);
            return;
        }
        setError('');
        setCurrentStep((s) => Math.min(s + 1, TOTAL_STEPS - 1));
    }

    function goBack() {
        setError('');
        setCurrentStep((s) => Math.max(s - 1, 0));
    }

    async function handleSendCode() {
        const email = form.email.trim();
        if (!email || !EMAIL_REGEX.test(email)) {
            setError('Enter a valid email on the previous step');
            return;
        }
        setError('');
        setSendingCode(true);
        try {
            await screenHostSignupSendCode(email);
            setCodeSent(true);
            posthog.capture('screen host signup email code sent', { email });
        } catch (err) {
            setError(err.response?.data?.error || 'Could not send verification code');
        } finally {
            setSendingCode(false);
        }
    }

    async function handleVerifyCode() {
        const email = form.email.trim();
        if (!/^\d{6}$/.test(otpCode.trim())) {
            setError('Enter the 6-digit code from your email');
            return;
        }
        setError('');
        setVerifyingCode(true);
        try {
            const res = await screenHostSignupVerifyCode(email, otpCode.trim());
            setEmailVerificationToken(res.data.emailVerificationToken);
            setEmailVerified(true);
            posthog.capture('screen host signup email verified', { email });
        } catch (err) {
            setError(err.response?.data?.error || 'Verification failed');
        } finally {
            setVerifyingCode(false);
        }
    }

    async function handleSubmit() {
        const msg = validateStep(6);
        if (msg) {
            setError(msg);
            return;
        }
        setError('');
        setSubmitting(true);
        try {
            const res = await screenHostSignup({
                emailVerificationToken,
                email: form.email.trim(),
                password: form.password,
                confirmPassword: form.confirmPassword,
                fname: form.fname.trim(),
                lname: form.lname.trim(),
                phoneNumber: form.phoneNumber.trim() || undefined,
                jobTitle: form.jobTitle.trim() || undefined,
                businessName: form.businessName.trim(),
                businessAddress: form.businessAddress.trim(),
                businessCity: form.businessCity.trim(),
                businessState: form.businessState.trim(),
                businessZipCode: form.businessZipCode.trim(),
                storeTypeId: selectedStoreType.value,
                storeTypeNotes: form.storeTypeNotes.trim() || undefined,
                screenCountRequested: selectedScreenCount.value,
                hardwareSetupPreference,
                agreementAccepted: true,
                agreementVersion,
                legalName: `${form.fname.trim()} ${form.lname.trim()}`.trim(),
            });
            const data = res.data;
            const fullName = `${data.fname} ${data.lname}`.trim();
            posthog.identify(form.email.trim(), {
                email: form.email.trim(),
                name: fullName,
                role: 'screen_host',
            });
            posthog.capture('screen host signup completed', { email: form.email.trim() });
            dispatch(save(data.token));
            persistUserSession({
                token: data.token,
                refreshToken: data.refreshToken,
                userId: data.userId,
                phoneNumber: data.phoneNumber,
                email: form.email.trim(),
            });
            persistRoleSession({
                role: 'user',
                token: data.token,
                refreshToken: data.refreshToken,
                name: fullName,
            });
            if (data.userId) {
                localStorage.setItem('userId', String(data.userId));
            }
            localStorage.setItem('name', fullName);
            window.location.href = '/user/compose?onboarding=1';
        } catch (err) {
            const apiErr = err.response?.data;
            setError(apiErr?.error || 'Signup failed. Please try again.');
            if (apiErr?.currentAgreementVersion) {
                setAgreementVersion(apiErr.currentAgreementVersion);
                setError('Agreement was updated. Please review and accept again.');
            }
            setSubmitting(false);
        }
    }

    function renderStepContent() {
        switch (currentStep) {
            case 0:
                return (
                    <>
                        <span className="advertiser-activate-title">Host screens with INVERT</span>
                        <span className="advertiser-activate-subtitle">
                            Create your account in a few minutes. Type what you want on screen — AI handles the rest,
                            and you earn when your displays aren&apos;t in use.
                        </span>
                        <ScreenInstallCarousel />
                        <p className="advertiser-signup-carousel-caption">
                            Real INVERT installations at restaurants, retail, and neighborhood businesses.
                        </p>
                    </>
                );
            case 1:
                return (
                    <>
                        <span className="advertiser-activate-title">Your store</span>
                        <div className="advertiser-activate-field">
                            <label htmlFor="host-business-name">Business name</label>
                            <input
                                id="host-business-name"
                                type="text"
                                required
                                value={form.businessName}
                                onChange={(e) => onChange('businessName', e.target.value)}
                            />
                        </div>
                        <div className="advertiser-activate-field">
                            <label htmlFor="host-business-address">Street address</label>
                            <input
                                id="host-business-address"
                                type="text"
                                required
                                value={form.businessAddress}
                                onChange={(e) => onChange('businessAddress', e.target.value)}
                            />
                        </div>
                        <div className="advertiser-activate-field">
                            <label htmlFor="host-business-city">City</label>
                            <input
                                id="host-business-city"
                                type="text"
                                required
                                value={form.businessCity}
                                onChange={(e) => onChange('businessCity', e.target.value)}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: 12 }}>
                            <div className="advertiser-activate-field" style={{ flex: 1 }}>
                                <label htmlFor="host-business-state">State</label>
                                <input
                                    id="host-business-state"
                                    type="text"
                                    required
                                    maxLength={10}
                                    value={form.businessState}
                                    onChange={(e) => onChange('businessState', e.target.value)}
                                />
                            </div>
                            <div className="advertiser-activate-field" style={{ flex: 1 }}>
                                <label htmlFor="host-business-zip">Zip code</label>
                                <input
                                    id="host-business-zip"
                                    type="text"
                                    required
                                    value={form.businessZipCode}
                                    onChange={(e) => onChange('businessZipCode', e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="advertiser-activate-field">
                            <label>Number of screens</label>
                            <Select
                                options={SCREEN_COUNT_OPTIONS}
                                value={selectedScreenCount}
                                onChange={(opt) => setSelectedScreenCount(opt)}
                            />
                        </div>
                    </>
                );
            case 2:
                return (
                    <>
                        <span className="advertiser-activate-title">Store type</span>
                        <div className="advertiser-activate-field">
                            <label>What type of business is this?</label>
                            <Select
                                options={storeTypes}
                                value={selectedStoreType}
                                onChange={(opt) => setSelectedStoreType(opt)}
                                placeholder="Select store type…"
                            />
                        </div>
                        <div className="advertiser-activate-field">
                            <label htmlFor="host-store-notes">
                                {isOtherStoreType ? 'Describe your store type' : 'Tell us more (optional)'}
                            </label>
                            <textarea
                                id="host-store-notes"
                                rows={3}
                                value={form.storeTypeNotes}
                                onChange={(e) => onChange('storeTypeNotes', e.target.value)}
                                placeholder="e.g. farm-to-table Italian, two dining rooms, busy lunch crowd"
                                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0' }}
                            />
                        </div>
                    </>
                );
            case 3:
                return (
                    <>
                        <span className="advertiser-activate-title">Contact details</span>
                        <div className="advertiser-activate-field">
                            <label htmlFor="host-fname">First name</label>
                            <input
                                id="host-fname"
                                type="text"
                                required
                                value={form.fname}
                                onChange={(e) => onChange('fname', e.target.value)}
                            />
                        </div>
                        <div className="advertiser-activate-field">
                            <label htmlFor="host-lname">Last name</label>
                            <input
                                id="host-lname"
                                type="text"
                                required
                                value={form.lname}
                                onChange={(e) => onChange('lname', e.target.value)}
                            />
                        </div>
                        <div className="advertiser-activate-field">
                            <label htmlFor="host-email">Email</label>
                            <input
                                id="host-email"
                                type="email"
                                required
                                value={form.email}
                                onChange={(e) => onChange('email', e.target.value)}
                            />
                        </div>
                        <div className="advertiser-activate-field">
                            <label htmlFor="host-phone">Phone (optional)</label>
                            <input
                                id="host-phone"
                                type="tel"
                                value={form.phoneNumber}
                                onChange={(e) => onChange('phoneNumber', e.target.value)}
                            />
                        </div>
                        <div className="advertiser-activate-field">
                            <label htmlFor="host-job-title">Your role (optional)</label>
                            <input
                                id="host-job-title"
                                type="text"
                                value={form.jobTitle}
                                onChange={(e) => onChange('jobTitle', e.target.value)}
                                placeholder="Owner, Manager, etc."
                            />
                        </div>
                    </>
                );
            case 4:
                return (
                    <>
                        <span className="advertiser-activate-title">Verify your email</span>
                        <span className="advertiser-activate-subtitle">
                            We sent a 6-digit code to{' '}
                            <strong className="advertiser-signup-email-highlight">{form.email.trim()}</strong>
                        </span>
                        {!codeSent ? (
                            <div className="advertiser-signup-send-code-wrap">
                                <button
                                    type="button"
                                    className="advertiser-activate-submit"
                                    onClick={handleSendCode}
                                    disabled={sendingCode}
                                >
                                    {sendingCode ? 'Sending…' : 'Send verification code'}
                                </button>
                            </div>
                        ) : (
                            <>
                                {emailVerified ? (
                                    <p className="advertiser-signup-verified-badge">Email verified</p>
                                ) : (
                                    <div className="advertiser-signup-otp-block">
                                        <SegmentedOtpInput
                                            value={otpCode}
                                            onChange={setOtpCode}
                                            disabled={verifyingCode}
                                            autoFocus={codeSent}
                                        />
                                        <div className="advertiser-signup-otp-actions">
                                            <button
                                                type="button"
                                                className="advertiser-activate-submit"
                                                onClick={handleVerifyCode}
                                                disabled={verifyingCode || otpCode.length < 6}
                                            >
                                                {verifyingCode ? 'Checking…' : 'Verify'}
                                            </button>
                                            <button
                                                type="button"
                                                className="advertiser-signup-btn-secondary"
                                                onClick={handleSendCode}
                                                disabled={sendingCode}
                                            >
                                                Resend code
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                );
            case 5:
                return (
                    <>
                        <span className="advertiser-activate-title">Create a password</span>
                        <div className="advertiser-activate-field">
                            <label htmlFor="host-password">Password</label>
                            <input
                                id="host-password"
                                type="password"
                                required
                                minLength={8}
                                value={form.password}
                                onChange={(e) => onChange('password', e.target.value)}
                            />
                        </div>
                        <div className="advertiser-activate-field">
                            <label htmlFor="host-confirm">Confirm password</label>
                            <input
                                id="host-confirm"
                                type="password"
                                required
                                minLength={8}
                                value={form.confirmPassword}
                                onChange={(e) => onChange('confirmPassword', e.target.value)}
                            />
                        </div>
                    </>
                );
            case 6:
                return (
                    <>
                        <span className="advertiser-activate-title">Almost done</span>
                        <span className="advertiser-activate-subtitle">
                            Choose how you&apos;ll run INVERT on your TVs, then accept the host agreement.
                        </span>
                        <fieldset className="advertiser-activate-field" style={{ border: 'none', padding: 0 }}>
                            <legend style={{ fontWeight: 600, marginBottom: 8 }}>How will you run INVERT?</legend>
                            <label className="advertiser-activate-checkbox" style={{ display: 'block', marginBottom: 8 }}>
                                <input
                                    type="radio"
                                    name="hardwareSetup"
                                    checked={hardwareSetupPreference === 'smart_tv'}
                                    onChange={() => setHardwareSetupPreference('smart_tv')}
                                />
                                <span>I have a Smart TV (Fire TV, Android TV, or Samsung)</span>
                            </label>
                            <label className="advertiser-activate-checkbox" style={{ display: 'block' }}>
                                <input
                                    type="radio"
                                    name="hardwareSetup"
                                    checked={hardwareSetupPreference === 'needs_chip'}
                                    onChange={() => setHardwareSetupPreference('needs_chip')}
                                />
                                <span>I need an INVERT chip — someone will reach out to confirm or ship one</span>
                            </label>
                        </fieldset>
                        <label className="advertiser-activate-checkbox">
                            <input
                                type="checkbox"
                                checked={agreementAccepted}
                                onChange={(e) => {
                                    setAgreementAccepted(e.target.checked);
                                    if (e.target.checked) setError('');
                                }}
                            />
                            <span>
                                I agree to the{' '}
                                <Link to="/screen-host-agreement" target="_blank" rel="noopener noreferrer">
                                    INVERT Screen Host Agreement
                                </Link>
                                {agreementVersion ? ` (version ${agreementVersion})` : ''}
                            </span>
                        </label>
                    </>
                );
            default:
                return null;
        }
    }

    return (
        <div className="advertiser-activate-page advertiser-signup-page">
            <SignupNav onLogoClick={() => navigate('/')} />
            <div className="advertiser-signup-steps">
                <div className="advertiser-signup-steps-progress">
                    {PROGRESS_LABELS.map((label, i) => (
                        <div
                            key={label}
                            className={`advertiser-signup-step-bar ${i <= currentStep ? 'active' : ''} ${i < currentStep ? 'completed' : ''}`}
                            title={label}
                        />
                    ))}
                </div>
            </div>
            <div className="advertiser-activate-main">
                <div className="advertiser-activate-card">
                    {renderStepContent()}
                    {error && <p className="advertiser-activate-error">{error}</p>}
                    <div className="advertiser-signup-actions">
                        {currentStep > 0 && (
                            <button type="button" className="advertiser-signup-btn-secondary" onClick={goBack}>
                                Back
                            </button>
                        )}
                        {currentStep < TOTAL_STEPS - 1 ? (
                            <button
                                type="button"
                                className="advertiser-activate-submit"
                                onClick={() => {
                                    if (currentStep === 4 && !emailVerified) {
                                        const v = validateStep(4);
                                        if (v) {
                                            setError(v);
                                            return;
                                        }
                                    }
                                    goNext();
                                }}
                            >
                                Continue
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="advertiser-activate-submit"
                                onClick={handleSubmit}
                                disabled={submitting}
                            >
                                {submitting ? 'Creating account…' : 'Create account'}
                            </button>
                        )}
                    </div>
                    <p className="advertiser-signup-footer-link">
                        Already have an account?{' '}
                        <button
                            type="button"
                            className="advertiser-activate-tos-link"
                            onClick={() => navigate('/screenHostLogin/')}
                        >
                            Log in
                        </button>
                    </p>
                </div>
            </div>
        </div>
    );
}
